import { ReactNode } from 'react';
import { useKeyboardConfig } from '@/hooks/useKeyboardConfig';
import { useStatusBarConfig } from '@/hooks/useStatusBarConfig';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useSplashScreen } from '@/hooks/useSplashScreen';
import { useDeepLinkAnalytics } from '@/hooks/useDeepLinkAnalytics';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { AppReviewProvider } from '@/components/providers/AppReviewProvider';
import { OfflineBanner } from '@/components/ui/OfflineBanner';

/**
 * Capacitor Provider Component
 *
 * Initializes and configures all Capacitor plugins for native mobile functionality:
 * - Splash Screen: Native loading screen during app initialization
 * - Keyboard: Layout adjustments, accessory bar, resize behavior
 * - Status Bar: Theme-aware styling, overlay mode
 * - App: Deep linking, URL handling, back button, app state
 * - Deep Link Analytics: Track deep link usage and performance
 * - Push Notifications: Token registration, foreground handling, tap navigation
 *
 * Must be placed inside:
 * - AuthProvider (for splash screen auth state)
 * - ThemeProvider (for status bar theme sync)
 * - BrowserRouter (for deep link navigation)
 *
 * Only runs on native platforms (iOS/Android), no-op on web.
 */
export function CapacitorProvider({ children }: { children: ReactNode }) {
  useSplashScreen();
  useKeyboardConfig();
  useStatusBarConfig();
  useAppConfig();
  useDeepLinkAnalytics();
  usePushNotifications();

  return (
    <>
      <AppReviewProvider />
      <OfflineBanner />
      {children}
    </>
  );
}
